// =============================================================================
// features/credit-analyses/components/DocumentReviewActions.tsx — Conferência
// humana de um documento (aprovar/rejeitar) — F46-S10.
//
// Renderizado por DocumentChecklistHeader para cada item do checklist em
// `pending_review` — é o único estado que o backend aceita revisar
// (PATCH /api/documents/:id/review responde 409 fora disso, F46-S06).
//
// Visível SÓ com `documents:review` — o caller (DocumentChecklistHeader)
// já faz esse gate, mas o componente também trava por segurança (defesa em
// profundidade, mesmo padrão de DocumentsPanel/useDocumentsGate).
//
// Rejeitar exige motivo em texto obrigatório (CHECK
// chk_documents_rejected_needs_reason no banco) — a UI bloqueia ANTES de
// chamar a API, mas o texto ainda pode conter PII do titular (LGPD): nunca é
// logado, só viaja no corpo da requisição.
// =============================================================================

import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../lib/api';
import type { ChecklistFlow } from '../hooks/useDocumentChecklist';
import { useReviewDocument } from '../hooks/useDocumentChecklist';

// ─── Helpers puros (testados em __tests__/document-checklist.test.tsx) ─────

/**
 * Valida o motivo de rejeição ANTES de chamar a API — espelha o CHECK
 * `chk_documents_rejected_needs_reason` do banco (F46-S06), mas devolve um
 * erro legível na UI em vez de deixar o 400 do backend ser o primeiro sinal.
 * Retorna `null` quando válido, a mensagem de erro caso contrário.
 */
export function validateRejectionReason(reason: string): string | null {
  return reason.trim().length === 0 ? 'Informe o motivo da rejeição.' : null;
}

// ─── Modal de rejeição (motivo obrigatório) ─────────────────────────────────

interface RejectModalProps {
  documentLabel: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

function RejectModal({
  documentLabel,
  isPending,
  onCancel,
  onConfirm,
}: RejectModalProps): React.JSX.Element {
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !isPending) onCancel();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPending, onCancel]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const validationError = validateRejectionReason(reason);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    onConfirm(reason.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reject-doc-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-md flex flex-col gap-5 p-6"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
      >
        <div className="flex flex-col gap-1.5">
          <h2
            id="reject-doc-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-lg)', letterSpacing: '-0.03em' }}
          >
            Rejeitar documento
          </h2>
          <p className="font-sans text-ink-2" style={{ fontSize: 'var(--text-sm)' }}>
            <strong>{documentLabel}</strong> voltará para pendência. O motivo é exibido ao operador
            que atende o cidadão.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="reject-reason"
              className="font-sans text-xs font-semibold text-ink-2 uppercase tracking-[0.1em]"
            >
              Motivo <span className="text-danger">*</span>
            </label>
            <textarea
              id="reject-reason"
              rows={4}
              required
              aria-invalid={error !== null || undefined}
              aria-describedby={error !== null ? 'reject-reason-error' : undefined}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error !== null) setError(null);
              }}
              placeholder="Ex.: foto ilegível, documento vencido, dados divergentes do cadastro."
              className="w-full font-sans text-sm font-medium text-ink bg-surface-1 rounded-sm px-[14px] py-[11px] border border-border-strong shadow-[inset_0_1px_2px_var(--border-inner-dark)] transition-[border-color,box-shadow] duration-fast ease placeholder:text-ink-4 hover:border-ink-3 focus:outline-none focus:border-azul focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)] resize-y min-h-[100px]"
              style={error !== null ? { borderColor: 'var(--danger)' } : undefined}
            />
            {error !== null && (
              <span id="reject-reason-error" role="alert" className="text-xs text-danger">
                {error}
              </span>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isPending}
              className="flex-1 justify-center"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={isPending}
              className="flex-1 justify-center"
            >
              {isPending ? 'Rejeitando…' : 'Confirmar rejeição'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export interface DocumentReviewActionsProps {
  documentId: string;
  documentLabel: string;
  flow: ChecklistFlow;
  contextId: string;
  /** Gate de segurança redundante — o caller já filtra por permissão. */
  canReview: boolean;
}

export function DocumentReviewActions({
  documentId,
  documentLabel,
  flow,
  contextId,
  canReview,
}: DocumentReviewActionsProps): React.JSX.Element | null {
  const { toast } = useToast();
  const review = useReviewDocument(flow, contextId);
  const [rejectOpen, setRejectOpen] = React.useState(false);

  if (!canReview) return null;

  function describeError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 409) {
        return 'Este documento já foi revisado ou não está pronto para conferência (verifique o scan antivírus).';
      }
      return err.message;
    }
    return 'Não foi possível registrar a conferência. Tente novamente.';
  }

  const handleApprove = (): void => {
    review.mutate(
      { documentId, body: { status: 'approved' } },
      {
        onSuccess: () => toast(`"${documentLabel}" aprovado.`, 'success'),
        onError: (err) => toast(describeError(err), 'danger'),
      },
    );
  };

  const handleReject = (reason: string): void => {
    review.mutate(
      { documentId, body: { status: 'rejected', rejectionReason: reason } },
      {
        onSuccess: () => {
          toast(`"${documentLabel}" rejeitado.`, 'info');
          setRejectOpen(false);
        },
        onError: (err) => toast(describeError(err), 'danger'),
      },
    );
  };

  return (
    <div className="flex items-center gap-2 shrink-0">
      <Button
        variant="secondary"
        size="sm"
        onClick={handleApprove}
        disabled={review.isPending}
        aria-label={`Aprovar ${documentLabel}`}
      >
        {review.isPending ? 'Aprovando…' : 'Aprovar'}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setRejectOpen(true)}
        disabled={review.isPending}
        aria-label={`Rejeitar ${documentLabel}`}
      >
        Rejeitar
      </Button>

      {rejectOpen && (
        <RejectModal
          documentLabel={documentLabel}
          isPending={review.isPending}
          onCancel={() => setRejectOpen(false)}
          onConfirm={handleReject}
        />
      )}
    </div>
  );
}
